import type { CSSProperties, ReactNode } from 'react';
import { cn } from '@/lib/cn';

/**
 * Shared pieces of the "Now" card family (NowCard / NowHabitCard /
 * NowMeetingCard). The three cards have genuinely different layouts, so
 * there's no forced single shell — but the status pill, title, and progress
 * bar were duplicated verbatim and live here instead.
 */

export function NowStatusPill({
  hue = 'brand',
  className,
  children,
}: {
  /** brand = task/habit "now" pill; info = read-only meeting pill. */
  hue?: 'brand' | 'info';
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 font-mono text-eyebrow font-medium uppercase',
        hue === 'brand' ? 'bg-brand-soft text-brand-ink' : 'bg-info-bg text-t1',
        className,
      )}
    >
      <span
        className={cn(
          'animate-now-pulse h-1.5 w-1.5 rounded-full',
          hue === 'brand' ? 'bg-brand' : 'bg-info',
        )}
        aria-hidden
      />
      {children}
    </div>
  );
}

export function NowTitle({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <h2 className={cn('text-title-lg font-medium leading-tight tracking-tight text-t1', className)}>
      {children}
    </h2>
  );
}

export function NowProgressBar({
  pct,
  className,
  fillClassName,
  fillStyle,
}: {
  /** 0–1. */
  pct: number;
  /** Track overrides (height, margin, rounding). */
  className?: string;
  fillClassName?: string;
  fillStyle?: CSSProperties;
}) {
  return (
    <div className={cn('h-[3px] overflow-hidden rounded-full bg-rail', className)} aria-hidden>
      <div
        className={cn('h-full rounded-full', fillClassName)}
        style={{ width: `${pct * 100}%`, ...fillStyle }}
      />
    </div>
  );
}
